import { Inject, Injectable, Optional } from '@angular/core';
import { LOCAL_STORAGE_CONFIG, LocalStorageConfig } from './storage.tokens';

type Millis = number;

interface WrappedValue<T> {
  /** actual data */
  d: T;
  /** expiry timestamp (epoch ms). If absent, no expiration */
  e?: Millis;
  /** stored app version (for debugging/auditing) */
  v?: string;
}

const VERSION_KEY = '__app_version__';

function now(): Millis {
  return Date.now();
}

function hasWindow(): boolean {
  try {
    return typeof window !== 'undefined';
  } catch {
    return false;
  }
}

/**
 *  ****** Local storage service ******
 *
 * !important: when updating version, make sure to update the version in the app.module.ts file
 *
 * @example
 * ``` set/get
 * const localStorageService = new LocalStorageService();
 * localStorageService.set('test_LOCALSTORAGE', { testingObject: true });
 * const state = localStorageService.get('test_LOCALSTORAGE');
 * console.log(state);
 * ```
 *
 * @example
 * ``` set with TTL
 * const localStorageService = new LocalStorageService();
 * localStorageService.set('test_LOCALSTORAGE', { testingObject: true }, { ttlMs: 15 * 60 * 1000 }); // 15 minutes
 * const state = localStorageService.get('test_LOCALSTORAGE');
 * console.log(state);
 * ```
 *
 * @example
 * ``` remove
 * const localStorageService = new LocalStorageService();
 * localStorageService.remove('test_LOCALSTORAGE');
 * ```
 *
 * @example
 * ``` clearNamespace (recommended on logout)
 * const localStorageService = new LocalStorageService();
 * localStorageService.clearNamespace();
 * ```
 *
 * @example
 * ``` clearNamespaceExcept
 * const localStorageService = new LocalStorageService();
 * localStorageService.clearNamespaceExcept(['key1', 'key2']);
 * ```
 *
 * @example
 * ``` clearAll (rarely needed)
 * const localStorageService = new LocalStorageService();
 * localStorageService.clearAll();
 * ```
 *
 * @example
 * ``` switch to sessionStorage
 * const localStorageService = new LocalStorageService();
 * const sessionStorageService = localStorageService.withSession();
 * ```
 */

@Injectable({ providedIn: 'root' })
export class LocalStorageService {
  private storage: StorageLike;
  private namespace = 'app';
  private version?: string;
  private preserveOnVersionChange: string[] = [];

  constructor(
    @Optional() @Inject(LOCAL_STORAGE_CONFIG) cfg?: LocalStorageConfig,
  ) {
    const ns = cfg?.namespace?.trim() || 'app';
    this.namespace = ns;
    this.version = cfg?.version;
    this.preserveOnVersionChange = cfg?.preserveOnVersionChange ?? [];

    const wantSession = cfg?.storage === 'session';
    this.storage = createStorage(wantSession ? 'session' : 'local');

    this.applyVersionGate();
  }

  /** Write value, optional TTL in ms */
  set<T>(key: string, value: T, opts?: { ttlMs?: Millis }): void {
    const payload: WrappedValue<T> = {
      d: value,
      v: this.version,
      ...(opts?.ttlMs ? { e: now() + opts.ttlMs } : {}),
    };
    this.storage.setItem(this.k(key), JSON.stringify(payload));
  }

  /** Read value; auto-delete if expired */
  get<T>(key: string): T | null {
    const raw = this.storage.getItem(this.k(key));
    if (!raw) return null;

    try {
      const parsed = JSON.parse(raw) as WrappedValue<T>;
      if (parsed.e && parsed.e <= now()) {
        // expired – clean up
        this.remove(key);
        return null;
      }
      return parsed.d;
    } catch {
      // corrupt payload – clean up
      this.remove(key);
      return null;
    }
  }

  /** Cache helper: return existing; otherwise compute, store, and return. */
  async getOrSet<T>(
    key: string,
    factory: () => Promise<T> | T,
    opts?: { ttlMs?: Millis },
  ): Promise<T> {
    const cached = this.get<T>(key);
    if (cached !== null) return cached;
    const fresh = await factory();
    this.set<T>(key, fresh, opts);
    return fresh;
  }

  /** Remove a single key */
  remove(key: string): void {
    this.storage.removeItem(this.k(key));
  }

  /** Clear ONLY this namespace */
  clearNamespace(): void {
    const prefix = this.nsPrefix();
    for (const k of this.storage.keys()) {
      if (k.startsWith(prefix)) this.storage.removeItem(k);
    }
    // keep version key (namespaced)
    this.setVersion(this.version);
  }

  /** Clear ONLY this namespace except whitelisted keys */
  clearNamespaceExcept(except: string[] = []): void {
    const prefix = this.nsPrefix();
    const exceptFull = new Set(except.map((k) => this.k(k)));
    const versionFull = this.k(VERSION_KEY);

    for (const k of this.storage.keys()) {
      if (!k.startsWith(prefix)) continue;
      if (k === versionFull) continue;
      if (exceptFull.has(k)) continue;
      this.storage.removeItem(k);
    }
  }

  /** Clear ALL keys in underlying store (rarely needed) */
  clearAll(): void {
    this.storage.clear();
    this.setVersion(this.version);
  }

  /** Does a namespaced key exist (not expired)? */
  exists(key: string): boolean {
    return this.get(key) !== null;
  }

  /** Switch to sessionStorage on the fly (returns a new instance-like facade) */
  withSession(): LocalStorageService {
    const clone = new LocalStorageService({
      namespace: this.namespace,
      version: undefined,
      preserveOnVersionChange: this.preserveOnVersionChange,
      storage: 'session',
    } as any); // satisfies constructor type
    return clone;
  }

  // ---------- internals ----------

  private k(key: string): string {
    return `${this.namespace}:${key}`;
  }
  private nsPrefix(): string {
    return `${this.namespace}:`;
  }

  private getVersion(): string | undefined {
    return this.storage.getItem(this.k(VERSION_KEY)) || undefined;
  }
  private setVersion(v?: string) {
    if (!v) return;
    this.storage.setItem(this.k(VERSION_KEY), v);
  }

  /** On version change: clear namespaced keys except preserves */
  private applyVersionGate(): void {
    if (!this.version) return; // no versioning configured
    const storedVer = this.getVersion();
    if (storedVer === this.version) return;

    // Version changed → clear namespaced keys except whitelisted ones
    const prefix = this.nsPrefix();
    const preserveFullKeys = new Set(
      this.preserveOnVersionChange.map((k) => this.k(k)),
    );

    for (const k of this.storage.keys()) {
      if (!k.startsWith(prefix)) continue;
      if (preserveFullKeys.has(k)) continue;
      this.storage.removeItem(k);
    }
    this.setVersion(this.version);
  }
}

/** Minimal Storage-like facade + safe fallbacks */
interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
  clear(): void;
  keys(): string[];
}

function createStorage(kind: 'local' | 'session'): StorageLike {
  // Browser available and storage usable?
  if (hasWindow()) {
    try {
      const s =
        kind === 'session' ? window.sessionStorage : window.localStorage;
      // probe
      const t = '__probe__';
      s.setItem(t, '1');
      s.removeItem(t);

      return {
        getItem: (k) => s.getItem(k),
        setItem: (k, v) => s.setItem(k, v),
        removeItem: (k) => s.removeItem(k),
        clear: () => s.clear(),
        keys: () => Object.keys(s),
      };
    } catch {
      /* fall through to memory */
    }
  }

  // Fallback (SSR or private mode blocks)
  const mem = new Map<string, string>();
  return {
    getItem: (k) => mem.get(k) ?? null,
    setItem: (k, v) => void mem.set(k, v),
    removeItem: (k) => void mem.delete(k),
    clear: () => void mem.clear(),
    keys: () => Array.from(mem.keys()),
  };
}
